import React, { useState, useEffect, useMemo } from 'react';
import { Badge, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import {
  getBankTransactionsTransactionIdRemittanceLines,
  patchBankTransactionsTransactionIdRemittanceLines,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  BankTransactionRemittanceLine,
  Invoice,
  JsonPatchOperation,
} from '../api/generated/model';
import InvoicePicker from './common/InvoicePicker';
import InvoiceDetailModal from './InvoiceDetailModal';

interface RemittancePanelProps {
  transactionId: string;
}

interface ManualEdit {
  id: string;            // existing line id, or `new:<temp>` for unsaved adds
  isNew: boolean;
  invoice: Invoice | null;
  amount: string;
}

const formatCurrency = (s?: string): string => {
  if (!s) return '';
  const n = parseFloat(s);
  if (Number.isNaN(n)) return s;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
};

const sourceLabel = (s?: string): string => {
  switch (s) {
    case 'edi820_rmr': return 'EDI 820';
    case 'external_memo': return 'External Memo';
    case 'note': return 'Note';
    case 'manual': return 'Manual';
    default: return s || '';
  }
};

const RemittancePanel: React.FC<RemittancePanelProps> = ({ transactionId }) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['remittance-lines', transactionId],
    queryFn: () => getBankTransactionsTransactionIdRemittanceLines(transactionId),
    enabled: !!transactionId,
  });

  const lines = useMemo<BankTransactionRemittanceLine[]>(() => {
    return ((data as any)?.lines as BankTransactionRemittanceLine[] | undefined) || [];
  }, [data]);
  const effective = (data as any)?.effective_source as 'bank' | 'manual' | undefined;

  // Bank-source lines (read-only).
  const bankLines = useMemo(
    () => lines.filter(l => l.source !== 'manual'),
    [lines]
  );
  // Manual-source lines (editable). Mirror them into local state so admin
  // can stage edits without committing per-keystroke.
  const serverManualLines = useMemo(
    () => lines.filter(l => l.source === 'manual'),
    [lines]
  );
  const [edits, setEdits] = useState<ManualEdit[]>([]);
  useEffect(() => {
    setEdits(serverManualLines.map(l => ({
      id: l.id || '',
      isNew: false,
      // The API resolves claimed_invoice_number to a full Invoice
      // server-side and returns it nested. Falls through to a stub
      // (number-only) when the claim doesn't resolve uniquely.
      invoice: (l.invoice as Invoice | undefined)
        ?? (l.claimed_invoice_number ? ({ invoice_number: l.claimed_invoice_number } as Invoice) : null),
      amount: l.amount || '',
    })));
  }, [serverManualLines]);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);

  // Diff editor state vs. server state to build a JSON-PATCH.
  const dirty = useMemo(() => {
    if (edits.length !== serverManualLines.length) return true;
    for (let i = 0; i < edits.length; i++) {
      const e = edits[i];
      const s = serverManualLines.find(x => x.id === e.id);
      if (!s) return true;
      const editedNumber = e.invoice?.invoice_number || '';
      if (editedNumber !== (s.claimed_invoice_number || '')) return true;
      if (e.amount !== (s.amount || '')) return true;
    }
    return false;
  }, [edits, serverManualLines]);

  const addRow = () => {
    setEdits(prev => [
      ...prev,
      { id: `new:${Date.now()}-${Math.random().toString(36).slice(2)}`, isNew: true, invoice: null, amount: '' },
    ]);
  };
  const updateInvoice = (id: string, invoice: Invoice | null) => {
    setEdits(prev => prev.map(r => r.id === id ? { ...r, invoice } : r));
  };
  const updateAmount = (id: string, amount: string) => {
    setEdits(prev => prev.map(r => r.id === id ? { ...r, amount } : r));
  };
  const removeRow = (id: string) => {
    setEdits(prev => prev.filter(r => r.id !== id));
  };

  const handleSave = async () => {
    setErr(null);
    const ops: JsonPatchOperation[] = [];

    // Removed: server lines whose id isn't in edits.
    const editIds = new Set(edits.filter(e => !e.isNew).map(e => e.id));
    for (const s of serverManualLines) {
      if (s.id && !editIds.has(s.id)) {
        ops.push({ op: 'remove' as any, path: `/${s.id}` });
      }
    }
    // Replaced: existing edits whose fields changed.
    for (const e of edits) {
      if (e.isNew) continue;
      const s = serverManualLines.find(x => x.id === e.id);
      if (!s) continue;
      const editedNumber = e.invoice?.invoice_number || '';
      if (editedNumber !== (s.claimed_invoice_number || '')) {
        ops.push({ op: 'replace' as any, path: `/${e.id}/claimed_invoice_number`, value: editedNumber });
      }
      if (e.amount !== (s.amount || '')) {
        ops.push({ op: 'replace' as any, path: `/${e.id}/amount`, value: e.amount });
      }
    }
    // Added: new edits.
    for (const e of edits) {
      if (!e.isNew) continue;
      if (!e.invoice || !e.amount) {
        setErr('Pick an invoice and enter an amount for every new manual row.');
        return;
      }
      const number = e.invoice.invoice_number || '';
      ops.push({
        op: 'add' as any,
        path: '/-',
        value: { claimed_invoice_number: number, amount: e.amount },
      });
    }

    if (ops.length === 0) return;
    setSaving(true);
    try {
      await patchBankTransactionsTransactionIdRemittanceLines(transactionId, ops);
      await refetch();
    } catch (error: any) {
      setErr(error?.response?.data?.message || `Failed to save: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>;
  }

  const overrideActive = effective === 'manual';

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 className="text-muted mb-0">Remittance</h6>
        {overrideActive && <Badge bg="info">Manual override active</Badge>}
        {effective === 'bank' && bankLines.length > 0 && <Badge bg="secondary">Bank-decoded</Badge>}
      </div>

      {err && <Alert variant="danger" onClose={() => setErr(null)} dismissible>{err}</Alert>}

      {/* Manual entries first — they're the override. */}
      <div className="border rounded p-2 mb-3" style={{ borderColor: overrideActive ? '#0dcaf0' : undefined }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="small text-muted">
            <strong className="text-body">Manual entries</strong>
            <span className="ms-2">
              {overrideActive
                ? '— overriding the bank-decoded set below.'
                : '— add one to override the bank-decoded set below.'}
            </span>
          </div>
          <Button size="sm" variant="outline-primary" onClick={addRow}>
            <i className="bi bi-plus me-1"></i>Add row
          </Button>
        </div>
        {edits.length === 0 ? (
          <div className="text-muted small fst-italic mb-1">No manual entries.</div>
        ) : (
          <table className="table table-sm mb-0">
            <thead>
              <tr className="text-muted small">
                <th style={{ fontWeight: 'normal' }}>Invoice</th>
                <th style={{ fontWeight: 'normal', width: '160px' }}>Amount</th>
                <th style={{ fontWeight: 'normal', width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {edits.map(e => (
                <tr key={e.id}>
                  <td>
                    <InvoicePicker
                      value={e.invoice}
                      onChange={(inv) => updateInvoice(e.id, inv)}
                      onOpenInvoiceDetail={setInvoiceModalId}
                      size="sm"
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={e.amount}
                      onChange={ev => updateAmount(e.id, ev.target.value)}
                      placeholder="0.00"
                    />
                  </td>
                  <td>
                    <Button size="sm" variant="outline-danger" onClick={() => removeRow(e.id)}>
                      <i className="bi bi-x"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="d-flex justify-content-end mt-1">
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Save Remittance
          </Button>
        </div>
      </div>

      {/* Bank-decoded second — visibly silenced when manual entries exist. */}
      {bankLines.length > 0 && (
        <div>
          <div className="small text-muted mb-1">
            <strong className="text-body">Bank-decoded</strong>
            {overrideActive && <span className="fst-italic ms-2">— silenced by the manual entries above.</span>}
          </div>
          <table className="table table-sm mb-0" style={{ opacity: overrideActive ? 0.5 : 1 }}>
            <thead>
              <tr className="text-muted small">
                <th style={{ fontWeight: 'normal', width: '110px' }}>Source</th>
                <th style={{ fontWeight: 'normal' }}>Invoice</th>
                <th style={{ fontWeight: 'normal', textAlign: 'right', width: '120px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {bankLines.map((l, i) => (
                <tr key={l.id || i}>
                  <td><Badge bg="secondary">{sourceLabel(l.source)}</Badge></td>
                  <td><code>{l.claimed_invoice_number}</code></td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {invoiceModalId && (
        <InvoiceDetailModal
          show
          invoiceId={invoiceModalId}
          onHide={() => setInvoiceModalId(null)}
        />
      )}
    </div>
  );
};

export default RemittancePanel;
